import styled from 'styled-components';

import { bidiText } from 'components/bidi';

import { ctrlBase, fieldContainer, fieldLabel, fieldMessage } from '../base';

export const inputContainer = styled.div`
  ${fieldContainer}
`;

export const input = styled('input')`
  ${ctrlBase}
  padding-right: var(--s6);
  cursor: pointer;

  &.mini {
    height: var(--h-btn-sm);
    font-size: var(--fs-label);
  }
`;

export const label = styled('label')<{ labelColor?: string | undefined }>`
  ${fieldLabel}
`;

export const message = styled.span`
  ${fieldMessage}
`;

export const icon = styled.div`
  position: absolute;
  right: var(--s3);
  bottom: 0;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: var(--text-muted);
  line-height: 0;

  &.mini {
    right: var(--s2);
  }
`;

export const iconSelect = styled.div`
  position: absolute;
  left: var(--s3);
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

/*
 * The floating option panel.
 *
 * Three things were wrong with it. It painted its own border while closed, so
 * a collapsed list left a grey rule under the field. It had no layer of its
 * own, so an open list interleaved with whatever card came after it in the
 * document. And it scrolled at a height tall enough to cover a sibling card
 * whole rather than reading as a list with more below.
 *
 * So: the surface is drawn only while open, the panel sits on --z-overlay,
 * and it scrolls at --h-overlay-max. `box-sizing: border-box` is global, so
 * max-height caps the border box and the padding collapses with it — the
 * closed panel is genuinely zero-height, not a padded sliver.
 */
export const dropDownItems = styled.ul`
  position: absolute;
  top: calc(100% + var(--s1));
  left: 0;
  width: 100%;
  z-index: var(--z-overlay);
  display: flex;
  flex-direction: column;
  gap: var(--s1);
  padding: var(--s2);
  margin: 0;
  list-style: none;
  background: var(--surface);
  border: 1px solid transparent;
  border-radius: var(--r-md);
  max-height: 0;
  overflow: hidden;
  visibility: hidden;
  transition: max-height 0.2s;

  &.show {
    max-height: var(--h-overlay-max);
    overflow-y: auto;
    overscroll-behavior: contain;
    visibility: visible;
    border-color: var(--line);
    box-shadow: var(--sh-2);
  }

  &::-webkit-scrollbar {
    width: var(--s2);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--line-strong);
    border-radius: var(--r-pill);
    border: 2px solid var(--surface);
  }
`;

/* One option — the design's `.check` row: a box, then the name. */
/* Option labels are payload — menu items, departments, people. */
export const dropDownItem = styled.li`
  ${bidiText};
  display: flex;
  align-items: center;
  gap: var(--s2);
  flex: 0 0 auto;
  padding: var(--s2) var(--s3);
  border: 1px solid var(--line);
  border-radius: var(--r-sm);
  background: var(--surface);
  cursor: pointer;
  transition:
    background-color 0.12s,
    border-color 0.12s;

  &:hover {
    border-color: var(--line-strong);
    background: var(--surface-alt);
  }

  &.active {
    border-color: var(--primary);
    background: var(--primary-soft);
  }
`;

export const text = styled.span`
  ${bidiText};
  font-size: var(--fs-body);
  font-weight: var(--fw-regular);
`;

export const img = styled.img``;

export const arrowImg = styled.img``;

export const InputBox = styled.div`
  position: relative;
`;

/* ── selected values — the design's `.tokens` / `.token` ───────────────── */

export const ActiveItems = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: var(--s1);
  margin-top: var(--s2);
  padding: 0;
  list-style: none;
`;

export const ActiveItem = styled.li`
  ${bidiText};
  display: inline-flex;
  align-items: center;
  gap: var(--s1);
  padding: var(--s1) var(--s1) var(--s1) var(--s2);
  border-radius: var(--r-sm);
  background: var(--primary-soft);
  color: var(--primary);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-body);
`;

export const ActiveItemButton = styled.button`
  display: grid;
  place-items: center;
  width: 15px;
  height: 15px;
  padding: 0;
  border: none;
  border-radius: var(--r-sm);
  background: none;
  color: inherit;
  cursor: pointer;
  line-height: 0;

  &:hover {
    background: var(--surface);
  }

  svg {
    width: 9px;
    height: 9px;
  }
`;
